import React, { useState, useEffect, useRef } from 'react';
import {
  useMaterialReactTable,
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_Cell,
  MRT_RowData,
  MRT_RowSelectionState,
} from 'material-react-table';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, IconButton, ThemeProvider, Typography, Checkbox, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'; // Import DeleteIcon
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Tabletheme } from '../../../styles/table.style';

export interface EditableColumnDef<T extends MRT_RowData> extends MRT_ColumnDef<T> {
  renderEditCell?: (params: {
    value: any;
    onChange: (value: any) => void;
    rowIndex: number;
  }) => React.ReactNode;
}

export interface EditableTableProps<T extends MRT_RowData> {
  data: T[];
  columns: EditableColumnDef<T>[];
  onSaveRow: (updatedRow: T) => void;
  onCellChange: (rowIndex: number, columnId: string, value: any) => void;
  editingRowIndex: number | null;
  onCancelEdit: (rowIndex: number) => void; // Pass rowIndex
  onEditRow: (rowIndex: number) => void;
  onDeleteRow: (rowIndex: number) => void; // Added onDeleteRow prop
  enableEditing?: boolean;
  cellHeight?: number;
  hideDelete?: boolean;
  hideEdit?: boolean;
  alwaysEditableRows?: string[];
  editingRowIndices?: number[];
  customeEmptyRecordText?: boolean;
  customeEmptyRecordComponent?: React.ReactNode;
  formtype?: string;
  TE_ID?: string;
  isSaving?: boolean;
  showSuccessIcon?: boolean; // New parameter
  enablePagination?: boolean;
  // Row selection props
  enableRowSelection?: boolean;
  selectedRowIndices?: number[];
  onSelectionChange?: (selectedIndices: number[]) => void;

  isV4?: boolean;
  enableSorting?: boolean;
  enableColumnFilters?: boolean;
}

export const EditableTable = <T extends MRT_RowData>({
  data,
  columns,
  onSaveRow,
  onCellChange,
  editingRowIndex,
  onCancelEdit,
  onEditRow,
  onDeleteRow,
  enableEditing = true,
  cellHeight = 60,
  hideDelete = false,
  hideEdit = false,
  alwaysEditableRows = [],
  editingRowIndices = [],
  customeEmptyRecordText = false,
  customeEmptyRecordComponent,
  formtype = '',
  TE_ID = '',
  isSaving = false,
  showSuccessIcon = false,
  enablePagination = true,
  enableRowSelection = false,
  selectedRowIndices = [],
  onSelectionChange,
  isV4,
  enableSorting = true,
  enableColumnFilters = false,
}: EditableTableProps<T>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const autoIsV4 = location.pathname.toLowerCase().includes('v4');
  const activeIsV4 = isV4 !== undefined ? isV4 : autoIsV4;
  
  // Row selection state management
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  
  // Initialize row selection from props
  useEffect(() => {
    if (enableRowSelection && selectedRowIndices.length > 0) {
      const initialSelection: MRT_RowSelectionState = {};
      selectedRowIndices.forEach(index => {
        initialSelection[index.toString()] = true;
      });
      setRowSelection(initialSelection);
    }
  }, [enableRowSelection, selectedRowIndices]);
  
  // Handle row selection change
  const handleRowSelectionChange = (updaterOrValue: any) => {
    const newSelection = typeof updaterOrValue === 'function' 
      ? updaterOrValue(rowSelection) 
      : updaterOrValue;
    
    setRowSelection(newSelection);
    
    // Convert to array of selected indices and call callback
    if (onSelectionChange) {
      const selectedIndices = Object.keys(newSelection)
        .filter(key => newSelection[key])
        .map(key => Number(key));
      onSelectionChange(selectedIndices);
    }
  };
  
  // Check if a row is selected
  const isRowSelected = (rowIndex: number) => {
    return enableRowSelection ? rowSelection[rowIndex.toString()] || false : true;
  };
  

  const table = useMaterialReactTable({
    data,
    enablePagination,
    enableSorting,
    enableColumnFilters,
    // Data changes (e.g. "Add" appending a row) must not yank the user back to
    // page 1 — the edit-row effect below moves pagination to the row being
    // edited, and the clamp effect keeps the index valid after deletes.
    autoResetPageIndex: false,
    enableRowSelection: enableRowSelection,
    getRowId: (row, index) => index.toString(),
    onRowSelectionChange: handleRowSelectionChange,
    state: {
      rowSelection: rowSelection,
    },
    layoutMode: 'grid',
    enableColumnResizing: activeIsV4,
    columnResizeMode: activeIsV4 ? 'onChange' : undefined,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Actions',
        size: 120,
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: {
          align: 'center',
          sx: { alignItems: 'flex-start', paddingTop: '10px' },
        },
      },
    },
    columns: columns.map((col) => ({
      ...col,
      Cell: ({ cell, row }: { cell: MRT_Cell<T>; row: MRT_Row<T> }) => {
        const isEditing = editingRowIndex === row.index;
        const cellValue = cell.getValue() as any;
        const accessorKey = col.accessorKey as string;
        const isCellEmpty = cellValue === null || cellValue === undefined || cellValue === '' || (Array.isArray(cellValue) && cellValue.length === 0);

        if ((isEditing && col.renderEditCell) || (alwaysEditableRows.includes(accessorKey) && col.renderEditCell && isCellEmpty)) {
          return col.renderEditCell({
            value: cellValue,
            onChange: (newValue: any) =>
              onCellChange(row.index, col.accessorKey as string, newValue),
            rowIndex: row.index,
          });
        }

        if (col.Cell && !(isEditing && col.renderEditCell)) {
          // If column provides a custom cell renderer, use it in read-only mode
          return (col.Cell as any)({
            cell,
            row,
            column: col,
            renderedCellValue: cellValue,
            table: undefined,
          });
        }

        if (Array.isArray(cellValue)) {
          return <span>{cellValue.join(', ')}</span>;
        }

        return <span>{cellValue}</span>;
      },
    })),

    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        color: '#ffffff', // Direct colors
        backgroundColor: '#2C4748',
        fontWeight: activeIsV4 ? 'bold' : undefined,
        borderRight: activeIsV4 ? '1px solid rgba(255, 255, 255, 0.15)' : undefined,
        // Long header captions wrap instead of clipping to "Product Raw..." (86ajey208)
        whiteSpace: activeIsV4 ? 'normal !important' : undefined,
        '& *': activeIsV4 ? {
          whiteSpace: 'normal !important',
          overflowWrap: 'break-word',
        } : undefined,
        '& .MuiTableSortLabel-root': {
          color: '#fff', // text color of the label
          '&:hover': {
            color: '#fff',
          },
          '&.Mui-active': {
            color: '#fff',
          },
        },
        '& .MuiTableSortLabel-icon': {
          color: '#fff !important', // force the icon to white
        },
      },
    }),
    enableTopToolbar: false,
    enableBottomToolbar: enablePagination,
    muiTableBodyCellProps: ({ column }) => ({
      sx: {
        minHeight: `${cellHeight}px`,
        height: 'auto',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        padding: activeIsV4 ? '8px 16px' : '16px', // Compact padding in V4
        borderRight: activeIsV4 ? '1px solid rgba(224, 224, 224, 0.5)' : undefined,
        borderBottom: activeIsV4 ? '1px solid rgba(44, 71, 72, 0.2) !important' : undefined,
        backgroundColor: activeIsV4 ? 'inherit !important' : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: column.id === 'mrt-row-actions' ? 'center' : 'flex-start',
        // Clear global form-element margins and override for strict vertical cell centering
        '& .MuiFormControl-root': activeIsV4 ? {
          margin: '0px !important',
          marginBottom: '0px !important',
        } : undefined,
        '& .MuiTextField-root': activeIsV4 ? {
          margin: '0px !important',
          marginBottom: '0px !important',
        } : undefined,
        '& .MuiInputBase-root': activeIsV4 ? {
          height: '34px !important', // Compact, uniform height for all inputs
        } : undefined,
        // Override helper text block-shift since table cells use custom tooltip error representations
        '& .MuiFormHelperText-root': activeIsV4 ? {
          display: 'none !important',
        } : undefined,
      },
    }),
    renderRowActions: ({ row }: { row: MRT_Row<T> }) => {
      const isRowSelectedForEditing = isRowSelected(row.index);
      const isDisabled = enableRowSelection && !isRowSelectedForEditing;
      
      return editingRowIndices.includes(row.index) ? (
        <Box display="flex" alignItems="center" justifyContent="center">
          <IconButton onClick={() => onSaveRow(data[row.index])} color="primary" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
          </IconButton>
          <IconButton onClick={() => onCancelEdit(row.index)} color="secondary" disabled={isSaving}> {/* Pass row index */}
            <CancelIcon />
          </IconButton>
        </Box>
      ) : editingRowIndex === row.index ? (
        <Box display="flex" alignItems="center" justifyContent="center">
          <IconButton onClick={() => onSaveRow(data[row.index])} color="primary" style={{ color: '#006EB3' }} disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
          </IconButton>
          <IconButton onClick={() => onCancelEdit(row.index)} color="secondary" style={{ color: 'rgb(244, 67, 54)' }} disabled={isSaving}> {/* Pass row index */}
            <CancelIcon />
          </IconButton>
        </Box>
      ) : showSuccessIcon ? (
        <Box display="flex" alignItems="center" justifyContent="center">
          <IconButton disabled color="success" style={{ color: '#4CAF50' }}>
            <CheckCircleIcon />
          </IconButton>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center">
          {
            !hideEdit ? (
              <IconButton
                disabled={(editingRowIndex !== null && editingRowIndex > -1) || isDisabled || isSaving}
                onClick={() => onEditRow(row.index)}
                color="default"
                style={{ opacity: isDisabled ? 0.5 : 1 }}
              >
                <EditIcon />
              </IconButton>
            ) : null
          }
          {
            !hideDelete ? (
              <IconButton 
                disabled={(editingRowIndex !== null && editingRowIndex > -1) || isDisabled || isSaving}
                onClick={() => onDeleteRow(row.index)} 
                color="error" 
                style={{ color: 'rgb(244, 67, 54)', opacity: isDisabled ? 0.5 : 1 }}
              >
                <DeleteIcon />
              </IconButton>
            ) : null
          }
        </Box>
      );
    },
    positionActionsColumn: 'last',
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<T> }) => {
      const isRowSelectedForEditing = isRowSelected(row.index);
      const isDisabled = enableRowSelection && !isRowSelectedForEditing;

      let rowBackgroundColor = 'inherit';
      if (editingRowIndex === row.index) {
        rowBackgroundColor = 'rgba(0, 0, 0, 0.04)'; // For the currently editing row
      } else if (enableRowSelection && row.getIsSelected()) {
        rowBackgroundColor = '#ffffff !important'; // Force white background for selected rows
      }
      
      return {
        sx: {
          backgroundColor: rowBackgroundColor,
          opacity: isDisabled ? 0.5 : 1,
          '&:hover': {
            backgroundColor: isDisabled ? 'inherit' : (activeIsV4 ? 'rgba(44, 71, 72, 0.04) !important' : 'rgba(0, 0, 0, 0.02)'),
          },
          // Override MRT's default selected row styling
          '&.Mui-selected': {
            backgroundColor: '#ffffff !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02) !important',
            },
          },
        },
      };
    },
    enableEditing: enableEditing,
    renderEmptyRowsFallback: ({ table }) => {
      return customeEmptyRecordText ? customeEmptyRecordComponent : null
    },
  });

  // With autoResetPageIndex off, deleting rows can leave the page index past
  // the last page — clamp it back so the table never renders an empty page.
  useEffect(() => {
    if (!enablePagination) return;
    const { pageIndex, pageSize } = table.getState().pagination;
    const maxPageIndex = Math.max(0, Math.ceil(data.length / pageSize) - 1);
    if (pageIndex > maxPageIndex) {
      table.setPageIndex(maxPageIndex);
    }
  }, [data.length, enablePagination]);

  // When a row enters edit mode (e.g. "Add" appends a row beyond the visible
  // page, or the page auto-reset to 1 after the data change), move pagination
  // to the page holding that row and focus its first editable field so the
  // user can start typing immediately.
  const tableRootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editingRowIndex === null || editingRowIndex < 0) return;
    if (enablePagination) {
      const { pageIndex, pageSize } = table.getState().pagination;
      const targetPage = Math.floor(editingRowIndex / pageSize);
      if (pageIndex !== targetPage) {
        table.setPageIndex(targetPage);
      }
    }
    const focusTimer = setTimeout(() => {
      const editableField = tableRootRef.current?.querySelector<HTMLElement>(
        'tbody input:not([disabled]):not([readonly]):not([type="checkbox"]), tbody textarea:not([disabled])'
      );
      editableField?.focus();
    }, 150);
    return () => clearTimeout(focusTimer);
  }, [editingRowIndex, data.length]);

  return (
    <ThemeProvider theme={Tabletheme}>
      <div ref={tableRootRef}>
        <MaterialReactTable table={table} />
      </div>
    </ThemeProvider>
  );
};
